"use client";

import React, { useState, useEffect } from "react";
import { useQuery, useMutation } from "@apollo/client";
import {
  Link2,
  MessageCircle,
  Check,
  Copy,
  Search,
  Send,
  Users,
  User,
  Loader2,
  Mail,
  MoreHorizontal,
  VolumeX,
  UserX,
} from "lucide-react";
import Image from "next/image";
import { toast } from "react-hot-toast";

import SlidingPanel from "@/components/ui/SlidingPanel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormattedPost } from "@/types";
import { ChatListItem, UserRef, StartChatInput } from "@/types/dm";
import {
  USER_MESSAGING,
  SEARCH_MENTIONS,
} from "@/api/apollo-client/queries/Queries";
import {
  START_CHAT,
  SEND_MESSAGE,
} from "@/api/apollo-client/mutations/mutations";
import { useAuth } from "@/contexts/authContext";
import defaultProfile from "@/images/default-profile.jpg";
import { plainPostText } from "@/utils/plainPostText";

interface CustomShare {
  url: string;
  title: string;
  text: string;
  imageUrl?: string;
  shareby?: string;
}

interface SharePanelProps {
  isOpen: boolean;
  onClose: () => void;
  post: FormattedPost | null;
  customShare?: CustomShare | null;
}

interface SearchUser {
  id: string;
  name: string;
  profile_picture?: {
    url: string;
  };
}

export default function SharePanel({
  isOpen,
  onClose,
  post,
  customShare,
}: SharePanelProps) {
  const { user } = useAuth();
  const [copiedLink, setCopiedLink] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedChats, setSelectedChats] = useState<Set<string>>(new Set());
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [isSharing, setIsSharing] = useState(false);
  const [searchUsers, setSearchUsers] = useState<SearchUser[]>([]);
  const [isDesktop, setIsDesktop] = useState(false);

  // Detect desktop vs mobile
  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 768); // md breakpoint
    };

    checkIsDesktop();
    window.addEventListener("resize", checkIsDesktop);

    return () => window.removeEventListener("resize", checkIsDesktop);
  }, []);

  // Get user's chats
  const { data: messagingData, loading: chatsLoading } = useQuery(
    USER_MESSAGING,
    {
      variables: {
        input: {
          pagination: {
            first: 50,
          },
        },
      },
      skip: !user?.id || !isOpen,
    }
  );

  // Search for users
  const { data: searchData, loading: searchLoading } = useQuery(
    SEARCH_MENTIONS,
    {
      variables: {
        name_prefix: searchQuery,
        first: 10,
      },
      skip: !searchQuery || searchQuery.length < 2,
    }
  );

  // Mutations
  const [startChat] = useMutation(START_CHAT);
  const [sendMessage] = useMutation(SEND_MESSAGE);

  const chats = messagingData?.userMessaging?.chats || [];

  useEffect(() => {
    if (searchData?.searchMentioned) {
      setSearchUsers(searchData.searchMentioned);
    }
  }, [searchData]);

  // Reset state when panel closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedChats(new Set());
      setSelectedUsers(new Set());
      setSearchQuery("");
      setCopiedLink(false);
      setIsSharing(false);
    }
  }, [isOpen]);

  const isClip = Boolean(post?.media?.some((m) => m.type === "video"));
  const isMarketplaceListing = (post?.type as any) === "MARKETPLACE_LISTING";
  const isSellerPage = (post?.type as any) === "SELLER_PAGE";
  const postUrl = customShare
    ? customShare.url
    : post
      ? isSellerPage
        ? `${window.location.origin}/marketplace/seller/${post.id}`
        : isMarketplaceListing
          ? `${window.location.origin}/marketplace/product/${post.id}`
          : `${window.location.origin}/${isClip ? "clips" : "post"}/${post.id}`
      : "";

  const shareTitle = customShare
    ? customShare.title
    : post
      ? isSellerPage
        ? `${post.author?.name || post.author?.display_name || "User"}'s Store`
        : isMarketplaceListing
          ? `${post.author?.name || post.handle || "User"}'s product`
          : `${post.author?.name || post.author?.display_name || "User"}'s ${isClip ? "clip" : "post"}`
      : "";

  const shareText = customShare
    ? customShare.text
    : post
      ? plainPostText(post.content || "")
      : "";

  const handleCopyLink = async () => {
    if (!postUrl) return;

    try {
      await navigator.clipboard.writeText(postUrl);
      setCopiedLink(true);
      toast.success("Link copied to clipboard!");

      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      console.error("Failed to copy link:", error);
      toast.error("Failed to copy link");
    }
  };

  const handleNativeShare = async () => {
    if ((!post && !customShare) || !postUrl) return;

    if (navigator.share) {
      try {
        const shareData: {
          title: string;
          text: string;
          url: string;
          files?: File[];
        } = {
          title: shareTitle,
          text: shareText,
          url: postUrl,
        };

        // Add image if available (for custom shares with QR code)
        if (customShare?.imageUrl) {
          try {
            // Fetch the image and convert to File for sharing
            const response = await fetch(customShare.imageUrl);
            const blob = await response.blob();
            const file = new File([blob], "qr-code.png", { type: "image/png" });
            shareData.files = [file];
          } catch (error) {
            console.warn("Could not attach image to share:", error);
            // Continue without image
          }
        }

        await navigator.share(shareData);
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          console.error("Error sharing:", error);
          toast.error("Failed to share");
        }
      }
    } else {
      // Fallback to copy link
      handleCopyLink();
    }
  };

  const toggleChatSelection = (chatId: string) => {
    const newSelected = new Set(selectedChats);
    if (newSelected.has(chatId)) {
      newSelected.delete(chatId);
    } else {
      newSelected.add(chatId);
    }
    setSelectedChats(newSelected);
  };

  const toggleUserSelection = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleShare = async () => {
    if (!post && !customShare) return;

    setIsSharing(true);

    try {
      // For custom shares (like QR code), only share the link (no media support in in-app messages)
      if (customShare) {
        // Share to selected existing chats
        for (const chatId of selectedChats) {
          await sendMessage({
            variables: {
              input: {
                chat_id: chatId,
                message_type: "general",
                message_content: `${customShare.text}\n${customShare.url}`,
              },
            },
          });
        }

        // Create new chats with selected users and share
        for (const userId of selectedUsers) {
          const startChatResult = await startChat({
            variables: {
              input: {
                chat_with: [userId],
                chat_type: "DM",
              },
            },
          });

          const newChatId = startChatResult.data?.startChat?.chat_id;
          if (newChatId) {
            await sendMessage({
              variables: {
                input: {
                  chat_id: newChatId,
                  message_type: "general",
                  message_content: `${customShare.text}\n${customShare.url}`,
                },
              },
            });
          }
        }

        const totalShares = selectedChats.size + selectedUsers.size;
        toast.success(
          `Link shared to ${totalShares} ${totalShares === 1 ? "chat" : "chats"}!`
        );
        onClose();
        return;
      }

      // Original post sharing logic
      if (post) {
        // Share to selected existing chats
        for (const chatId of selectedChats) {
          const hasVideo = isClip;
          const messageType = hasVideo ? "clip_share" : "post_share";
          const formattedText = plainPostText(post.content || "");
          const shareLabel = isMarketplaceListing
            ? "product"
            : hasVideo
              ? "clip"
              : "post";

          await sendMessage({
            variables: {
              input: {
                chat_id: chatId,
                message_type: messageType,
                shared_post_id: post.id,
                message_content: `Shared a ${shareLabel}: ${formattedText.slice(0, 100)}${
                  formattedText.length > 100 ? "..." : ""
                }`,
              },
            },
          });
        }

        // Create new chats with selected users and share
        for (const userId of selectedUsers) {
          const startChatResult = await startChat({
            variables: {
              input: {
                chat_with: [userId],
                chat_type: "DM",
              },
            },
          });

          const newChatId = startChatResult.data?.startChat?.chat_id;
          if (newChatId) {
            const hasVideo = isClip;
            const messageType = hasVideo ? "clip_share" : "post_share";
            const formattedText = plainPostText(post.content || "");
            const shareLabel = isMarketplaceListing
              ? "product"
              : hasVideo
                ? "clip"
                : "post";

            await sendMessage({
              variables: {
                input: {
                  chat_id: newChatId,
                  message_type: messageType,
                  shared_post_id: post.id,
                  message_content: `Shared a ${shareLabel}: ${formattedText.slice(0, 100)}${
                    formattedText.length > 100 ? "..." : ""
                  }`,
                },
              },
            });
          }
        }

        const totalShares = selectedChats.size + selectedUsers.size;
        toast.success(
          `Post shared to ${totalShares} ${totalShares === 1 ? "chat" : "chats"}!`
        );
        onClose();
      }
    } catch (error) {
      console.error("Error sharing:", error);
      toast.error("Failed to share");
    } finally {
      setIsSharing(false);
    }
  };

  const getChatDisplayInfo = (chat: ChatListItem) => {
    const currentUserId = user?.id;
    const ownerId = chat.chat_owner?.id;
    const withId = chat.chat_with?.id;
    const isOwnerMe = currentUserId && ownerId && currentUserId === ownerId;
    const otherUser = isOwnerMe ? chat.chat_with : chat.chat_owner;
    const isGroup =
      (typeof chat.chat_type === "string" &&
        chat.chat_type?.toUpperCase() === "GROUP_CHAT") ||
      (typeof chat.chat_type === "number" && chat.chat_type === 2) ||
      (chat.chat_members?.length || 0) > 2;

    const displayName = isGroup
      ? chat.chat_name || "Group"
      : otherUser?.name || "User";
    const groupAvatarUrl = chat?.chat_profile_picture?.url || "";
    const avatarUrl = isGroup
      ? groupAvatarUrl
      : otherUser?.profile_picture?.url || "";

    return {
      displayName,
      avatarUrl,
      isGroup,
    };
  };

  const filteredChats = chats.filter((chat: ChatListItem) => {
    if (!searchQuery) return true;
    const { displayName } = getChatDisplayInfo(chat);
    return displayName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const filteredUsers = searchUsers.filter(
    (user) =>
      !chats.some((chat: ChatListItem) => {
        const { isGroup } = getChatDisplayInfo(chat);
        if (isGroup) return false;

        const currentUserId = user?.id;
        const ownerId = chat?.chat_owner?.id;
        const isOwnerMe = currentUserId && ownerId && currentUserId === ownerId;
        const otherUser = isOwnerMe ? chat?.chat_with : chat?.chat_owner;

        return otherUser?.id === user.id;
      })
  );

  const handlePlatformShare = (platform: string) => {
    if ((!post && !customShare) || !postUrl) return;

    const title = shareTitle;
    const text = shareText;
    const encodedUrl = encodeURIComponent(postUrl);
    const encodedText = encodeURIComponent(`${title}: ${text}`);
    const encodedTitle = encodeURIComponent(title);

    let shareUrl = "";

    switch (platform) {
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
        break;
      case "facebook":
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        break;
      case "linkedin":
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case "email":
        shareUrl = `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`;
        break;
      case "telegram":
        shareUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
        break;
      default:
        return;
    }

    // Open in new window/tab
    if (platform === "email") {
      window.location.href = shareUrl;
    } else {
      window.open(shareUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (!post && !customShare) return null;

  // Shared content for both modal and sliding panel
  const shareContent = (
    <div
      className="space-y-6"
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
    >
      {/* Direct Link Section */}
      <div className="space-y-3">
        <h3 className="font-medium text-foreground flex items-center gap-2">
          <Link2 className="w-4 h-4" />
          Direct Link
        </h3>

        <div className="flex gap-2">
          <div className="flex-1 p-3 bg-muted rounded-lg text-sm text-muted-foreground break-all">
            {postUrl}
          </div>
          <Button
            onClick={handleCopyLink}
            variant="outline"
            size="sm"
            className="flex-shrink-0"
          >
            {copiedLink ? (
              <Check className="w-4 h-4 text-green-500" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
        </div>

        {/* Platform Share Buttons */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-muted-foreground">
            Share to Platform
          </h4>

          {/* Horizontal scrollable platform buttons */}
          <div
            className="flex gap-3 overflow-x-auto pb-2 scrollbar-none"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            onTouchStart={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            onTouchEnd={(e) => e.stopPropagation()}
          >
            {/* WhatsApp */}
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handlePlatformShare("whatsapp");
              }}
              className="flex flex-col items-center gap-2 h-auto py-3 px-4 flex-shrink-0 min-w-[70px]"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.785" />
              </svg>
              <span className="text-xs font-medium">WhatsApp</span>
            </Button>

            {/* Email */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePlatformShare("email")}
              className="flex flex-col items-center gap-2 h-auto py-3 px-4 flex-shrink-0 min-w-[70px]"
            >
              <Mail className="w-7 h-7 text-gray-600" />
              <span className="text-xs font-medium">Email</span>
            </Button>

            {/* Facebook */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePlatformShare("facebook")}
              className="flex flex-col items-center gap-2 h-auto py-3 px-4 flex-shrink-0 min-w-[70px]"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#1877F2">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
              <span className="text-xs font-medium">Facebook</span>
            </Button>

            {/* Twitter/X */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePlatformShare("twitter")}
              className="flex flex-col items-center gap-2 h-auto py-3 px-4 flex-shrink-0 min-w-[70px]"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#000000">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <span className="text-xs font-medium">Twitter</span>
            </Button>

            {/* LinkedIn */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePlatformShare("linkedin")}
              className="flex flex-col items-center gap-2 h-auto py-3 px-4 flex-shrink-0 min-w-[70px]"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#0A66C2">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              <span className="text-xs font-medium">LinkedIn</span>
            </Button>

            {/* Telegram */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePlatformShare("telegram")}
              className="flex flex-col items-center gap-2 h-auto py-3 px-4 flex-shrink-0 min-w-[70px]"
            >
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="#26A5E4">
                <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
              </svg>
              <span className="text-xs font-medium">Telegram</span>
            </Button>

            {/* More options */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleNativeShare}
              className="flex flex-col items-center gap-2 h-auto py-3 px-4 flex-shrink-0 min-w-[70px]"
            >
              <MoreHorizontal className="w-7 h-7 text-muted-foreground" />
              <span className="text-xs font-medium">More</span>
            </Button>
          </div>
        </div>
      </div>

      {/* In-App Sharing Section */}
      <div className="space-y-3">
        <h3 className="font-medium text-foreground flex items-center gap-2">
          <MessageCircle className="w-4 h-4" />
          Share in App
        </h3>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search chats or users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Chat/User List */}
        <div
          className="max-h-48 overflow-y-auto space-y-1 z-10"
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          onTouchEnd={(e) => e.stopPropagation()}
          onWheel={(e) => e.stopPropagation()}
        >
          {chatsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : (
            <>
              {/* Existing Chats */}
              {filteredChats.map((chat: ChatListItem) => {
                const { displayName, avatarUrl, isGroup } =
                  getChatDisplayInfo(chat);
                const isSelected = selectedChats.has(chat.id);

                // Get current user's settings for this chat (direct access)
                const currentUserSettings = chat.current_user_chat_settings;

                const isMuted = currentUserSettings?.chat_muted || false;
                const isBlocked = currentUserSettings?.chat_blocked || false;

                return (
                  <div
                    key={chat.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleChatSelection(chat.id);
                    }}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-primary/10 border border-primary/20"
                        : "hover:bg-muted"
                    } ${isBlocked ? "opacity-60" : ""}`}
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {avatarUrl ? (
                        <Image
                          src={avatarUrl || defaultProfile}
                          alt={displayName}
                          width={40}
                          height={40}
                          className="w-10 h-10 rounded-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = defaultProfile.src;
                          }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          {isGroup ? (
                            <Users className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <span className="text-sm font-semibold text-muted-foreground">
                              {displayName?.charAt(0).toUpperCase() || "U"}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-foreground truncate">
                          {displayName}
                        </p>
                        {isMuted && (
                          <VolumeX className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        )}
                        {isBlocked && (
                          <UserX className="w-4 h-4 text-red-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {isBlocked
                          ? "This user is blocked"
                          : isGroup
                            ? `${chat.chat_members?.length || 0} members`
                            : ""}
                      </p>
                    </div>

                    {/* Selection indicator */}
                    {isSelected && (
                      <Check className="w-5 h-5 text-primary flex-shrink-0" />
                    )}
                  </div>
                );
              })}

              {/* Search Results - New Users */}
              {searchQuery.length >= 2 && (
                <>
                  {searchLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                  ) : (
                    filteredUsers.map((searchUser) => {
                      const isSelected = selectedUsers.has(searchUser.id);

                      return (
                        <div
                          key={`user-${searchUser.id}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleUserSelection(searchUser.id);
                          }}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            isSelected
                              ? "bg-primary/10 border border-primary/20"
                              : "hover:bg-muted"
                          }`}
                        >
                          {/* Avatar */}
                          <div className="flex-shrink-0">
                            <Image
                              src={
                                searchUser.profile_picture?.url ||
                                defaultProfile
                              }
                              alt={searchUser.name}
                              width={40}
                              height={40}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          </div>

                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground truncate">
                              {searchUser.name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Start new chat
                            </p>
                          </div>

                          {/* Selection indicator */}
                          {isSelected && (
                            <Check className="w-5 h-5 text-primary" />
                          )}
                        </div>
                      );
                    })
                  )}
                </>
              )}

              {/* No results */}
              {!chatsLoading &&
                filteredChats.length === 0 &&
                filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    {searchQuery
                      ? "No chats or users found"
                      : "No chats available"}
                  </div>
                )}
            </>
          )}
        </div>

        {/* Share Button */}
        {(selectedChats.size > 0 || selectedUsers.size > 0) && (
          <Button onClick={handleShare} disabled={isSharing} className="w-full">
            {isSharing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Sharing...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Share to {selectedChats.size + selectedUsers.size}{" "}
                {selectedChats.size + selectedUsers.size === 1
                  ? "chat"
                  : "chats"}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  // Render desktop modal or mobile sliding panel
  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg mx-auto max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{customShare ? "Share" : "Share Post"}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto pr-2 -mr-2">
            {shareContent}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Mobile sliding panel
  return (
    <SlidingPanel
      isOpen={isOpen}
      onClose={onClose}
      title={customShare ? "Share" : "Share Post"}
      slideFrom="bottom"
      maxHeight="85vh"
      className="max-w-lg mx-auto"
    >
      {shareContent}
    </SlidingPanel>
  );
}
